<script lang="ts" setup>
import { __ } from '@@/bits/intl'
import OzAvatar from '@@/library/v4/components/OzAvatar.vue'
import OzBackButton from '@@/library/v4/components/OzBackButton.vue'
import OzTextButton, { OzTextButtonColorScheme } from '@@/library/v4/components/OzTextButton.vue'
import { LtiScreen, useLtiStore } from '@@/pinia/lti_store'
import LtiAuthOrgLoginWithPasswordForm from '@@/vuecomponents/LtiAuthOrgLoginWithPasswordForm.vue'
import LtiAuthOrgLoginWithTokenForm from '@@/vuecomponents/LtiAuthOrgLoginWithTokenForm.vue'
import { onMounted, ref } from 'vue'

const ltiStore = useLtiStore()

const xLoginWithPasswordForm = ref(true) // Login with token if false

onMounted(() => {
  // Show timeout screen 5 seconds earlier than the actual token expiration
  const timeoutScreenDelayInSeconds = ltiStore.stateTokenTimeoutSeconds - 5
  setTimeout(() => {
    ltiStore.setExpiryErrorScreen()
  }, timeoutScreenDelayInSeconds * 1000)
})

function resetLoginState(): void {
  ltiStore.setUsername('')
  ltiStore.setUsernameValidationMessage('')
  ltiStore.setPassword('')
  ltiStore.setPasswordValidationMessage('')
  ltiStore.setLinkDeploymentToken('')
  ltiStore.setTokenValidationMessage('')
}

function goBack(): void {
  resetLoginState()
  ltiStore.setCurrentScreen(LtiScreen.OrgVerification)
}

function showLoginWithTokenForm(): void {
  xLoginWithPasswordForm.value = false
}

function showLoginWithPasswordForm(): void {
  xLoginWithPasswordForm.value = true
}
</script>

<template>
  <div class="relative flex flex-col items-center tablet-landscape:items-start w-full h-full">
    <div :class="['fixed flex items-center box-border w-full pt-4 ps-6']">
      <OzBackButton :disabled="ltiStore.isAwaitingServerResponse" @click="goBack" />
    </div>

    <main
      :class="[
        'fixed bottom-0',
        'flex flex-col',
        'box-border w-full p-4 pb-6',
        'tablet-portrait:relative tablet-portrait:w-[375px] tablet-portrait:p-0 tablet-portrait:px-4 tablet-portrait:mt-16',
        'tablet-landscape:ml-auto tablet-landscape:mr-[calc(0.5*100vw)] tablet-landscape:rtl:mr-auto tablet-landscape:rtl:ml-[calc(0.5*100vw)]',
      ]"
    >
      <OzAvatar
        v-if="ltiStore.tenantLogo"
        :height="40"
        :width="40"
        class="rounded-full ml-2 mb-2.5"
        :src="ltiStore.tenantLogo"
        :alt-text-name="ltiStore.tenantName"
      />

      <div class="mb-5 text-dark-text-100 dark:text-light-text-100">
        <p class="px-2 mb-1 text-login-heading-1 font-semibold">
          {{ __('Connect to Backpack') }}
        </p>
        <p class="px-2 text-login-body">{{ ltiStore.tenantName }}</p>
      </div>

      <template v-if="xLoginWithPasswordForm">
        <div class="mb-5 text-dark-text-100 dark:text-light-text-100">
          <p class="px-2 text-login-body">
            {{ __('You can connect by logging in if you are a teacher or owner.') }}
          </p>
        </div>
        <LtiAuthOrgLoginWithPasswordForm class="mb-6" />
        <p
          class="text-body-small-posts text-dark-text-200 dark:text-light-text-200 text-center tablet-portrait:text-start px-2 tablet-portrait:mb-4"
        >
          <span>{{ __('Alternatively, you can use a token to do so if you are the owner.') }}</span>
          <OzTextButton
            :color-scheme="OzTextButtonColorScheme.Secondary"
            :text="__('Log in with token')"
            class="underline! font-semibold"
            @click="showLoginWithTokenForm"
          />
        </p>
      </template>
      <template v-else>
        <div class="mb-5 text-dark-text-100 dark:text-light-text-100">
          <p class="px-2 text-login-body">
            {{ __('You can connect by using a token if you are the owner or admin.') }}
          </p>
        </div>
        <LtiAuthOrgLoginWithTokenForm class="mb-6" />
        <p
          class="text-body-small-posts text-dark-text-200 dark:text-light-text-200 text-center tablet-portrait:text-start px-2 tablet-portrait:mb-4"
        >
          <span>{{ __('Alternatively, you can log in to do so if you are a teacher or owner.') }}</span>
          <OzTextButton
            :color-scheme="OzTextButtonColorScheme.Secondary"
            :text="__('Log in with password')"
            class="underline! font-semibold"
            @click="showLoginWithPasswordForm"
          />
        </p>
      </template>
    </main>
  </div>
</template>
